import { Layout } from 'antd';
import React from 'react';

import type { BreadcrumbItem } from '@/components/layout/AppBreadcrumb';
import { SidebarNav, SidebarNavItem } from '@/components/layout/SidebarNav';
import { TopBar } from '@/components/layout/TopBar';

/** Props for the `AppShell` full-page layout component. */
export interface AppShellProps {
  topNavItems: SidebarNavItem[];
  workspaceNavItems: SidebarNavItem[];
  activeNavKey: string;
  onNavigate: (path: string) => void;
  onOpenProfile: () => void;
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  userName: string;
  onSignOut: () => void;
  showAddAsset?: boolean;
  onOpenAddAsset?: () => void;
  chatPanelOpen?: boolean;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const { Content } = Layout;

/** Main application chrome: collapsible sidebar, top bar, scrollable content area, and optional sticky footer. */
export const AppShell: React.FC<AppShellProps> = ({
  topNavItems,
  workspaceNavItems,
  activeNavKey,
  onNavigate,
  onOpenProfile,
  title,
  subtitle,
  breadcrumbs,
  userName,
  onSignOut,
  showAddAsset,
  onOpenAddAsset,
  chatPanelOpen = false,
  footer,
  children,
}) => {
  const [navCollapsed, setNavCollapsed] = React.useState(false);
  const shellClassName = chatPanelOpen ? 'app-shell app-shell--chat-open' : 'app-shell';
  const hasFooter = footer !== undefined && footer !== null;

  return (
    <Layout className={shellClassName}>
      <SidebarNav
        topItems={topNavItems}
        workspaceItems={workspaceNavItems}
        activeKey={activeNavKey}
        onNavigate={onNavigate}
        collapsed={navCollapsed}
        onToggleCollapsed={() => setNavCollapsed((current) => !current)}
      />
      <Layout className="app-shell__main">
        <TopBar
          title={title}
          subtitle={subtitle}
          breadcrumbs={breadcrumbs}
          onNavigate={onNavigate}
          userName={userName}
          onOpenProfile={onOpenProfile}
          onSignOut={onSignOut}
          showAddAsset={showAddAsset}
          onOpenAddAsset={onOpenAddAsset}
        />
        <Content className="app-shell__content">
          <div className="app-shell__content-inner">{children}</div>
        </Content>
        {hasFooter ? <div className="app-shell__footer">{footer}</div> : null}
      </Layout>
    </Layout>
  );
};

export default AppShell;
